import React, { useEffect, useState } from 'react'

import { selectOwnsDeck } from 'redux/deck/selectors'
import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import TabContainer from 'components/elements/TabButtons'
import RootSettings from 'components/deckPage/settings/forms/RootSettings'
import ColorTagSettings from 'components/deckPage/settings/forms/ColorTags'
import FeaturedDeckImage from 'components/deckPage/settings/forms/FeaturedDeckImage'
import CategorySettings from 'components/deckPage/settings/forms/Categories'
import CollaboratorSettings from 'components/deckPage/settings/forms/Collaborators'
import DeckTagSettings from 'components/deckPage/settings/forms/DeckTags'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import PlaygroupForm from 'components/deckPage/settings/forms/PlaygroupForm'

import styles from './deckSettingsOverlay.module.scss'
import { DeckResponse } from 'services/apiTypes/deck.types'

type ViewType =
  | 'deckSettings'
  | 'collaborators'
  | 'deckTags'
  | 'colorTags'
  | 'categories'
  | 'featuredDeckImage'
  | 'playgroupForm'

const viewTypes: ViewType[] = [
  'deckSettings',
  'collaborators',
  'colorTags',
  'categories',
  'playgroupForm',
  'deckTags',
  'featuredDeckImage',
]

const DeckSettingsOverlay = () => {
  const modals = useAppSelector(state => state.active.modals)
  const initialDescription = useAppSelector(state => state.deck.description)

  const [editedDescription, setEditedDescription] = useState(initialDescription)

  const [open, setOpen] = useState(false)
  const [unsavedWarning, setUnsavedWarning] = useState(false)

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const handleWarningClose = () => {
    if (editedDescription !== initialDescription) return setUnsavedWarning(true)

    handleClose()
  }

  const handleClose = () => {
    const updatedModals = viewTypes.reduce((acc, viewType) => ({ ...acc, [viewType]: false }), {})

    setActiveState({ modals: updatedModals })
    setUnsavedWarning(false)
  }

  useEffect(() => {
    const open = viewTypes.some(viewType => modals[viewType])

    setOpen(open)
  }, [modals])

  return (
    <GlobalOverlayStack open={open} onClose={handleWarningClose} className={styles.container} id="deck-settings">
      <DeckSettingsContent
        onDeleteDeck={handleClose}
        onDescriptionEdited={setEditedDescription}
        unsavedWarning={unsavedWarning}
        setUnsavedWarning={setUnsavedWarning}
      />
    </GlobalOverlayStack>
  )
}

export default DeckSettingsOverlay

type Props = {
  onDeleteDeck: () => void
  onDescriptionEdited?: (description: string) => void
  unsavedWarning?: boolean
  setUnsavedWarning?: (unsavedWarning: boolean) => void
  onDeckSaved?: (res: DeckResponse) => void
}

export const DeckSettingsContent = ({
  onDeleteDeck,
  onDescriptionEdited,
  unsavedWarning,
  setUnsavedWarning,
  onDeckSaved,
}: Props) => {
  const modals = useAppSelector(state => state.active.modals)

  const ownsDeck = useAppSelector(selectOwnsDeck)
  const deckId = useAppSelector(state => state.deck.id)

  const initialDescription = useAppSelector(state => state.deck.description)
  const [editedDescription, setEditedDescription] = useState(initialDescription)

  const [view, setView] = useState<ViewType>('deckSettings')

  const handleEditDescription = (description: string) => {
    if (onDescriptionEdited) onDescriptionEdited(description)

    setEditedDescription(description)
  }

  useEffect(() => {
    const view = viewTypes.find(viewType => modals[viewType])

    if (view) setView(view)
  }, [modals])

  return (
    <>
      <TabContainer
        className={styles.tabs}
        optionsClassName={styles.tabButton}
        options={[
          {
            label: 'Main Settings',
            onClick: () => setView('deckSettings'),
            selected: view === 'deckSettings',
            disabled: !ownsDeck,
            className: view === 'deckSettings' ? styles.activeTab : '',
          },
          {
            icon: undefined,
            label: 'Collaborators',
            onClick: () => setView('collaborators'),
            selected: view === 'collaborators',
            className: view === 'collaborators' ? styles.activeTab : '',
            disabled: !deckId,
          },

          {
            icon: undefined,
            label: 'Color tags',
            onClick: () => setView('colorTags'),
            selected: view === 'colorTags',
            className: view === 'colorTags' ? styles.activeTab : '',
          },
          {
            icon: undefined,
            label: 'Categories',
            onClick: () => setView('categories'),
            selected: view === 'categories',
            className: view === 'categories' ? styles.activeTab : '',
          },
          {
            icon: undefined,
            label: (
              <span className={styles.playgroupHeader}>
                Power level
                {/* Don't love how this looks in menus, leaving it out for now */}
                {/* <img src="/images/playgroup_gg.svg" alt="Playgroup dot gg logo" height={15} width={15} /> */}
              </span>
            ),
            onClick: () => setView('playgroupForm'),
            selected: view === 'playgroupForm',
            className: view === 'playgroupForm' ? styles.activeTab : '',
          },
          {
            icon: undefined,
            label: 'Deck tags',
            onClick: () => setView('deckTags'),
            selected: view === 'deckTags',
            className: view === 'deckTags' ? styles.activeTab : '',
            disabled: !deckId,
          },
          {
            icon: undefined,
            label: 'Featured deck image',
            onClick: () => setView('featuredDeckImage'),
            selected: view === 'featuredDeckImage',
            className: view === 'featuredDeckImage' ? styles.activeTab : '',
            disabled: !deckId,
          },
        ]}
      />

      {view === 'deckSettings' && ownsDeck && (
        <RootSettings
          key={deckId}
          description={editedDescription}
          onDescriptionChange={handleEditDescription}
          onDeckUpdated={onDeckSaved}
        />
      )}
      {view === 'collaborators' && <CollaboratorSettings />}
      {view === 'deckTags' && <DeckTagSettings />}
      {view === 'colorTags' && <ColorTagSettings />}
      {view === 'categories' && <CategorySettings />}
      {view === 'featuredDeckImage' && <FeaturedDeckImage />}
      {view === 'playgroupForm' && <PlaygroupForm />}

      <ConfirmDeleteModal
        usedInOverlay
        open={!!unsavedWarning}
        onClose={() => setUnsavedWarning && setUnsavedWarning(false)}
        buttonText="Close without saving"
        header="Unsaved description changes"
        onConfirm={onDeleteDeck}
        message="You have unsaved changes to the description. Are you sure you want to close without saving?"
      />
    </>
  )
}
